/**
 * Reconciliation Page (T106, T109)
 * Payments grouped by method with subtotals and CSV export
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Download, CreditCard, FileText, Filter } from 'lucide-react';
import { useToast } from '../components/ui';
import {
  useReconciliation,
  useExportPaymentsCSV,
  formatCurrency,
  formatDate,
  PAYMENT_METHOD_LABELS,
  PAYMENT_STATUS_CONFIG,
  type PaymentMethod,
} from '../hooks/usePayments';

const METHODS: PaymentMethod[] = [
  'CREDIT_CARD',
  'DEBIT_CARD',
  'BANK_TRANSFER',
  'CASH',
  'CHECK',
  'APPLE_PAY',
  'GOOGLE_PAY',
  'OTHER',
];

// Default to current month
function getDefaultRange() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return {
    dateFrom: `${year}-${month}-01`,
    dateTo: now.toISOString().split('T')[0],
  };
}

export default function ReconciliationPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const defaults = getDefaultRange();

  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [methodFilter, setMethodFilter] = useState<PaymentMethod | undefined>(undefined);
  const [expandedMethod, setExpandedMethod] = useState<string | null>(null);

  const { data, isLoading, error } = useReconciliation({
    dateFrom,
    dateTo,
    method: methodFilter,
  });

  const exportCSV = useExportPaymentsCSV();

  const handleExport = async () => {
    try {
      await exportCSV.mutateAsync({ dateFrom, dateTo, method: methodFilter });
      showToast('success', 'CSV exported successfully');
    } catch {
      showToast('error', 'Failed to export CSV');
    }
  };

  if (error) {
    return (
      <div className="space-y-6 pb-8">
        <button
          onClick={() => navigate('/payments')}
          className="claude-button claude-button-ghost text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Payments
        </button>
        <div className="claude-card border-destructive/50 bg-destructive/5 p-6">
          <div className="flex items-center gap-3 text-destructive">
            <FileText className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Failed to load reconciliation data</p>
              <p className="text-sm mt-1 opacity-90">Please try refreshing the page</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const methodEntries = data ? Object.entries(data.byMethod) : [];

  return (
    <div className="space-y-8 pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/payments')}
              className="claude-button claude-button-ghost p-2"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1>Reconciliation</h1>
              <p className="text-muted-foreground text-lg">
                Payments grouped by method with subtotals
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exportCSV.isPending || !data}
          className="claude-button claude-button-primary"
        >
          <Download className="w-4 h-4" />
          {exportCSV.isPending ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {/* Filters */}
      <div className="claude-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Date Range & Method</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">From Date *</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="claude-input w-full"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">To Date *</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="claude-input w-full"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Payment Method</label>
            <select
              value={methodFilter || ''}
              onChange={(e) =>
                setMethodFilter(e.target.value ? (e.target.value as PaymentMethod) : undefined)
              }
              className="claude-input w-full"
            >
              <option value="">All Methods</option>
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-center space-y-4">
            <div className="claude-spinner mx-auto"></div>
            <p className="text-muted-foreground">Loading reconciliation data...</p>
          </div>
        </div>
      ) : data ? (
        <>
          {/* Grand Total Card */}
          <div className="claude-card p-6 bg-success/5 border-success/20">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Grand Total ({data.dateFrom} to {data.dateTo})
                </p>
                <p className="text-5xl font-semibold tracking-tight text-success">
                  {formatCurrency(data.grandTotal)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {data.totalCount} completed payment{data.totalCount !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="w-16 h-16 rounded-xl bg-success/10 flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-success" />
              </div>
            </div>
          </div>

          {/* Method Breakdown */}
          {methodEntries.length === 0 ? (
            <div className="claude-card">
              <div className="claude-empty-state">
                <CreditCard className="claude-empty-state-icon" />
                <h3 className="claude-empty-state-title">No payments in this period</h3>
                <p className="claude-empty-state-description">
                  Try adjusting the date range or filters
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {methodEntries
                .sort(([, a], [, b]) => b.total - a.total)
                .map(([method, group]) => (
                  <div key={method} className="claude-card overflow-hidden">
                    {/* Method header — clickable to expand */}
                    <button
                      onClick={() => setExpandedMethod(expandedMethod === method ? null : method)}
                      className="w-full p-6 flex items-center justify-between hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-primary" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold">
                            {PAYMENT_METHOD_LABELS[method as PaymentMethod] || method}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {group.count} payment{group.count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-semibold">{formatCurrency(group.total)}</p>
                        <p className="text-xs text-muted-foreground">
                          {((group.total / data.grandTotal) * 100).toFixed(1)}% of total
                        </p>
                      </div>
                    </button>

                    {/* Expanded payment list */}
                    {expandedMethod === method && (
                      <div className="border-t border-border">
                        <table className="claude-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Customer</th>
                              <th>Invoice</th>
                              <th>Amount</th>
                              <th>Reference</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.payments.map((payment: any) => {
                              const statusCfg =
                                PAYMENT_STATUS_CONFIG[
                                  payment.status as keyof typeof PAYMENT_STATUS_CONFIG
                                ] || PAYMENT_STATUS_CONFIG.COMPLETED;
                              return (
                                <tr key={payment.id}>
                                  <td className="text-muted-foreground whitespace-nowrap">
                                    {formatDate(payment.paidAt || payment.createdAt)}
                                  </td>
                                  <td className="font-medium text-sm">
                                    {payment.customerName || '—'}
                                  </td>
                                  <td className="text-sm">{payment.invoiceNumber || '—'}</td>
                                  <td className="font-semibold">
                                    {formatCurrency(payment.amount)}
                                  </td>
                                  <td className="text-muted-foreground text-xs font-mono">
                                    {payment.transactionId || '—'}
                                  </td>
                                  <td>
                                    <span className={statusCfg.className}>{statusCfg.label}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
